/**
 * Floating appointment details card with extension-driven sections and default manage actions.
 */

import React from 'react';
import { Alert, Button, Space, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  UserDeleteOutlined,
  UserOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StructuredNoteContent } from '@/components/structured-note';
import { ResourceLink } from './ResourceLink';
import {
  formatAppointmentTimeRange,
  getAppointmentStatusLabel,
  getCalendarAppointmentDurationMinutes,
  getCalendarAppointmentHeadline,
  getStatusTagColor,
  isServiceCalendarBooking,
  canMarkAppointmentNoShow
} from '../calendar/calendarUtils';
import { ServiceBookingDetailsSection } from './ServiceBookingDetailsSection';
import { AppointmentDetailRow } from './AppointmentDetailRow';
import { AppointmentDetailsBox } from './AppointmentDetailsBox';
import { AppointmentDetailSectionLabel } from './AppointmentDetailSectionLabel';
import { useAppointmentClientContact } from './useAppointmentClientContact';
import { type Appointment } from '@/api/mcp-appointments';
import { useGetResourcePolicyQuery } from '@/api/mcp-appointments/queries';
import {
  getAppointmentDisplayExtensions,
  getMeetingInfo,
  parseAppointmentContext
} from '../extensions/extension-utils';
import type { AppointmentDetailAction, AppointmentDetailSection } from '../extensions/extension-types';
import { useAuth } from '@/hooks/useAuth';
import { isAdminEquivalentRole } from '@/utils/authz';
import { isCancellationAllowed, leadTimeMinutesToDays } from '@/utils/policyLeadTime';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { DetailsPopupFrame } from '@/components/ui/DetailsPopupFrame';

const { Text } = Typography;

/** Props for the floating appointment details popup. */
interface AppointmentDetailsPopupProps {
  /** Appointment record rendered in the popup. */
  appointment: Appointment;
  /** Whether the popup should render. */
  visible: boolean;
  /** Whether Floating UI has finished positioning the popup. */
  isPositioned?: boolean;
  /** Closes the popup without mutating the appointment. */
  onClose: () => void;
  /** Confirms a scheduled appointment. */
  onConfirm: (appointment: Appointment) => void;
  /** Reserved for AI-assisted reschedule entry points. */
  onReschedule: (appointment: Appointment) => void;
  /** Opens manual reschedule for the appointment. */
  onManualReschedule?: (appointment: Appointment) => void;
  /** Cancels the appointment when policy allows. */
  onCancel?: (appointment: Appointment) => void;
  /** Marks customer no-show when attendance can be evaluated. */
  onMarkNoShow?: (appointment: Appointment) => void;
  /** Deletes the appointment; typically admin-only. */
  onDelete?: (appointment: Appointment) => void;
  /** Appointment id with an in-flight confirm mutation, if any. */
  confirmingAppointmentId: string | null;
  /** Floating UI positioning styles for the card. */
  floatingStyles?: React.CSSProperties;
  /** Ref callback used by Floating UI for the card. */
  floatingRef?: (node: HTMLElement | null) => void;
  /** Hides built-in confirm/reschedule/cancel actions while keeping extension actions. */
  hideDefaultActions?: boolean;
  /** Opens the tenant review flow for a pending customer change request. */
  onReviewChangeRequest?: (appointment: Appointment) => void;
  /** Opens the note editor; the notes section is read-only when omitted. */
  onEditNotes?: (appointment: Appointment) => void;
}

/** Renders an extension-provided section with the shared section label. */
function ExtensionSection({ section }: { section: AppointmentDetailSection }) {
  return (
    <div>
      {section.title ? <AppointmentDetailSectionLabel>{section.title}</AppointmentDetailSectionLabel> : null}
      {section.content}
    </div>
  );
}

/** Renders an extension-provided action button for the given appointment. */
function ExtensionActionButton({ action, appointment }: { action: AppointmentDetailAction; appointment: Appointment }) {
  return (
    <Button
      type={action.variant || 'text'}
      icon={action.icon}
      onClick={() => action.onClick(appointment)}
      danger={action.danger}
      disabled={action.disabled}>
      {action.label}
    </Button>
  );
}

/** Renders appointment details, extension sections, and manage actions in a floating card. */
export const AppointmentDetailsPopup: React.FC<AppointmentDetailsPopupProps> = ({
  appointment,
  visible,
  isPositioned = true,
  onClose,
  onConfirm,
  onManualReschedule,
  onCancel,
  onMarkNoShow,
  onDelete,
  confirmingAppointmentId,
  floatingStyles,
  floatingRef,
  hideDefaultActions = false,
  onReviewChangeRequest,
  onEditNotes
}) => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation('manage');
  const { payload } = useAuth();
  const { copy } = useCopyToClipboard();
  const isAdmin = isAdminEquivalentRole(payload?.role);
  const clientContact = useAppointmentClientContact(appointment);

  const { data: resourcePolicy } = useGetResourcePolicyQuery(
    appointment.resourceId,
    visible && !!appointment.resourceId
  );
  const isScheduled = appointment.status === 'SCHEDULED';
  const isConfirmed = appointment.status === 'CONFIRMED';
  const isInProgress = appointment.status === 'IN_PROGRESS';
  const canShowDefaultActions = !hideDefaultActions && (isScheduled || isConfirmed || isInProgress);
  const canShowNoShowAction = !hideDefaultActions && !!onMarkNoShow && canMarkAppointmentNoShow(appointment);
  const canReschedule = isScheduled || isConfirmed || isInProgress;
  const canCancelByStatus = isScheduled || isConfirmed || isInProgress;
  const hasActiveChangeRequest = appointment.activeChangeRequest?.status === 'PENDING';
  const cancellationLeadTimeMinutes = resourcePolicy?.cancellationLeadTimeMinutes ?? 0;
  const cancellationAllowed =
    isInProgress || isCancellationAllowed(appointment.startTime, cancellationLeadTimeMinutes);
  const cancelLeadTimeDays = leadTimeMinutesToDays(cancellationLeadTimeMinutes);
  const isConfirming = confirmingAppointmentId === appointment.id;

  if (!visible) return null;

  // Get display extensions that match this appointment's context source.
  // This prevents unrelated module extensions from rendering sections/actions.
  const displayExtensions = getAppointmentDisplayExtensions(appointment);
  const parsedContext = parseAppointmentContext(appointment);
  const contextData = parsedContext?.data || {};
  const meeting = getMeetingInfo(appointment);
  const isServiceBooking = isServiceCalendarBooking(appointment);
  const headline = getCalendarAppointmentHeadline(appointment) || t('appointmentComponents.details.title');
  const durationMinutes = getCalendarAppointmentDurationMinutes(appointment);

  const handleCopyMeetingLink = (link: string, linkLabel: string) =>
    copy(link, {
      successMessage: t('appointmentComponents.details.meeting.copied', { link: linkLabel }),
      errorMessage: t('appointmentComponents.details.meeting.copyFailed')
    });

  // Merge sections and actions from matching extensions (unique keys per module)
  const customSections = displayExtensions.flatMap((ext) =>
    (ext.getDetailSections?.(appointment, contextData) || []).map((s) => ({
      ...s,
      id: `${ext.moduleId}-${s.id}`,
    }))
  );
  const customActions = displayExtensions.flatMap((ext) =>
    (ext.getDetailActions?.(appointment, contextData) || []).map((a) => ({
      ...a,
      key: `${ext.moduleId}-${a.key}`,
    }))
  );

  // Sort sections by order
  const sortedSections = [...customSections].sort((a, b) => (a.order || 0) - (b.order || 0));
  const beforeSections = sortedSections.filter(s => s.position === 'before');
  const afterSections = sortedSections.filter(s => s.position === 'after');

  // Sort actions by order
  const sortedActions = [...customActions].sort((a, b) => (a.order || 0) - (b.order || 0));
  const beforeActions = sortedActions.filter(a => a.position === 'before');
  const afterActions = sortedActions.filter(a => a.position === 'after');

  const hasFooterActions =
    beforeActions.length > 0 ||
    afterActions.length > 0 ||
    canShowDefaultActions ||
    canShowNoShowAction ||
    (hasActiveChangeRequest && !!onReviewChangeRequest);

  const meetingLinks = [
    meeting.meetingModeratorUrl
      ? {
          key: 'moderator',
          url: meeting.meetingModeratorUrl,
          label: t('appointmentComponents.details.meeting.moderatorLink'),
          join: t('appointmentComponents.details.meeting.joinAsModerator')
        }
      : null,
    meeting.meetingUrl
      ? {
          key: 'member',
          url: meeting.meetingUrl,
          label: t('appointmentComponents.details.meeting.memberLink'),
          join: t('appointmentComponents.details.meeting.join')
        }
      : null
  ].filter((link): link is NonNullable<typeof link> => link !== null);

  return (
    <DetailsPopupFrame
      floatingRef={floatingRef}
      floatingStyles={floatingStyles}
      isPositioned={isPositioned}
      width={440}
      onClose={onClose}
    >
      {/* Header: what, when, status */}
      <div
        style={{
          padding: `${token.paddingSM}px ${token.paddingMD}px`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: token.marginSM
        }}>
        <span
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: token.borderRadius,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: token.colorPrimaryBg,
            color: token.colorPrimary,
            fontSize: token.fontSizeLG
          }}>
          <CalendarOutlined />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong ellipsis={{ tooltip: headline }} style={{ fontSize: token.fontSizeLG, display: 'block' }}>
            {headline}
          </Text>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {formatAppointmentTimeRange(appointment.startTime, appointment.endTime, i18n.language)}
            {' · '}
            {t('appointmentComponents.details.duration', { count: durationMinutes })}
          </Text>
          <div style={{ marginTop: token.marginXS }}>
            <Tag color={getStatusTagColor(appointment.status)} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
              {getAppointmentStatusLabel(appointment.status, t)}
            </Tag>
          </div>
        </div>
        <Space size={2} style={{ flexShrink: 0 }}>
          {isAdmin && onDelete ? (
            <Tooltip title={t('appointmentComponents.details.delete')}>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                aria-label={t('appointmentComponents.details.delete')}
                onClick={() => onDelete(appointment)}
              />
            </Tooltip>
          ) : null}
          <Tooltip title={t('appointmentComponents.details.close')}>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              aria-label={t('appointmentComponents.details.close')}
              onClick={onClose}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Scrollable content */}
      <div style={{ padding: token.paddingMD, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {isScheduled ? (
            <Alert type="info" showIcon title={t('appointmentComponents.details.unconfirmedAction')} />
          ) : null}

          {beforeSections.map((section) => (
            <ExtensionSection key={section.id} section={section} />
          ))}

          {isServiceBooking ? <ServiceBookingDetailsSection appointment={appointment} visible={visible} /> : null}

          <AppointmentDetailsBox>
              <AppointmentDetailRow icon={<UserOutlined />} label={t('appointmentComponents.details.labels.client')}>
                <Text strong>{clientContact.name}</Text>
                {clientContact.phone ? (
                  <div>
                    <a href={`tel:${clientContact.phone}`} style={{ fontSize: token.fontSizeSM }}>
                      <PhoneOutlined style={{ marginInlineEnd: 6 }} />
                      {clientContact.phone}
                    </a>
                  </div>
                ) : null}
                {clientContact.email ? (
                  <div style={{ minWidth: 0 }}>
                    <a
                      href={`mailto:${clientContact.email}`}
                      style={{ fontSize: token.fontSizeSM, wordBreak: 'break-all' }}>
                      <MailOutlined style={{ marginInlineEnd: 6 }} />
                      {clientContact.email}
                    </a>
                  </div>
                ) : null}
              </AppointmentDetailRow>

              {/* Resource row — direct resource bookings only; service bookings list their resources above */}
              {!isServiceBooking ? (
                <AppointmentDetailRow icon={<HomeOutlined />} label={t('appointmentComponents.details.labels.resource')}>
                  <ResourceLink resourceId={appointment.resourceId} />
                </AppointmentDetailRow>
              ) : null}

              <AppointmentDetailRow
                icon={<VideoCameraOutlined />}
                label={t('appointmentComponents.details.labels.format')}>
                <Tag color={meeting.format === 'VIDEO' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
                  {meeting.format === 'VIDEO'
                    ? t('appointmentComponents.details.meeting.video')
                    : t('appointmentComponents.details.meeting.inPerson')}
                </Tag>
                {meetingLinks.map((link) => (
                  <div key={link.key} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      <VideoCameraOutlined style={{ marginInlineEnd: 6 }} />
                      {link.join}
                    </a>
                    <Tooltip title={t('appointmentComponents.details.meeting.copy')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        aria-label={`${t('appointmentComponents.details.meeting.copy')}: ${link.label}`}
                        onClick={() => handleCopyMeetingLink(link.url, link.label)}
                      />
                    </Tooltip>
                  </div>
                ))}
              </AppointmentDetailRow>
          </AppointmentDetailsBox>

          {/* Notes: read-only, or editable when the host provides an editor */}
          {appointment.notes || onEditNotes ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <AppointmentDetailSectionLabel icon={<FileTextOutlined />}>
                  {t('appointmentComponents.details.notes')}
                </AppointmentDetailSectionLabel>
                {onEditNotes ? (
                  <Button
                    type="link"
                    size="small"
                    icon={appointment.notes ? <EditOutlined /> : <PlusOutlined />}
                    onClick={() => onEditNotes(appointment)}
                    style={{ paddingInline: 0, marginBottom: token.marginXS }}>
                    {appointment.notes
                      ? t('appointmentComponents.details.notesSection.edit')
                      : t('appointmentComponents.details.notesSection.add')}
                  </Button>
                ) : null}
              </div>
              <div
                style={{
                  padding: `${token.paddingSM}px ${token.paddingSM + 4}px`,
                  borderRadius: token.borderRadiusLG,
                  background: token.colorFillQuaternary,
                  border: `1px solid ${token.colorBorderSecondary}`
                }}>
                {appointment.notes ? (
                  <StructuredNoteContent value={appointment.notes} />
                ) : (
                  <Text type="secondary">{t('appointmentComponents.details.notesSection.empty')}</Text>
                )}
              </div>
            </div>
          ) : null}

          {afterSections.map((section) => (
            <ExtensionSection key={section.id} section={section} />
          ))}
        </Space>
      </div>

      {/* Footer actions — provided by extensions and/or the default manage actions */}
      {hasFooterActions ? (
        <div
          style={{
            padding: `${token.paddingSM}px ${token.paddingMD}px`,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillQuaternary,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: token.marginXS,
            flexWrap: 'wrap'
          }}>
          {beforeActions.map((action) => (
            <ExtensionActionButton key={action.key} action={action} appointment={appointment} />
          ))}
          {canShowNoShowAction ? (
            <Button
              danger
              icon={<UserDeleteOutlined />}
              onClick={() => onMarkNoShow?.(appointment)}
              loading={isConfirming}>
              {t('appointmentComponents.details.markNoShow')}
            </Button>
          ) : null}
          {canShowDefaultActions && onCancel && canCancelByStatus ? (
            <Tooltip
              title={
                !cancellationAllowed
                  ? t('appointmentComponents.details.cancelLeadTimeDisabled', { days: cancelLeadTimeDays })
                  : undefined
              }>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => onCancel(appointment)}
                disabled={!cancellationAllowed}>
                {t('appointmentComponents.details.cancel')}
              </Button>
            </Tooltip>
          ) : null}
          {canShowDefaultActions && onManualReschedule && canReschedule ? (
            <Button icon={<EditOutlined />} onClick={() => onManualReschedule(appointment)}>
              {t('appointmentComponents.details.reschedule')}
            </Button>
          ) : null}
          {hasActiveChangeRequest && onReviewChangeRequest ? (
            <Button type="primary" icon={<FileSearchOutlined />} onClick={() => onReviewChangeRequest(appointment)}>
              {t('appointmentComponents.changeRequest.review')}
            </Button>
          ) : null}
          {canShowDefaultActions && isScheduled ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => onConfirm(appointment)}
              loading={isConfirming}>
              {t('appointmentComponents.details.confirm')}
            </Button>
          ) : null}
          {afterActions.map((action) => (
            <ExtensionActionButton key={action.key} action={action} appointment={appointment} />
          ))}
        </div>
      ) : null}
    </DetailsPopupFrame>
  );
};
